import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  Brain,
  ClipboardCheck,
  Scale,
  Lock,
  ShieldCheck,
  Eye,
  ScrollText,
  CheckCircle,
  ArrowRight,
  Clock,
  Activity,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const offerings = [
  {
    icon: ClipboardCheck,
    title: 'AI Policy & Acceptable Use',
    description:
      'Draft clear, enforceable AI acceptable-use policies that your teams will actually follow - defining what is permitted, prohibited, and permitted-with-controls.',
  },
  {
    icon: Scale,
    title: 'AI Risk Classification',
    description:
      'Build a tiered risk taxonomy and a register of AI use cases, so high-risk applications get the scrutiny they need while low-risk experimentation stays unblocked.',
  },
  {
    icon: Lock,
    title: 'Data Protection for AI',
    description:
      'Establish PII/PHI boundaries, DLP-style controls at the point of AI access, and defences against prompt injection and data leakage into third-party models.',
  },
  {
    icon: ShieldCheck,
    title: 'Model & Vendor Assurance',
    description:
      'Assess AI vendors and models for data handling, training practices, security posture, and exit provisions - the same rigour you apply to any critical supplier.',
  },
  {
    icon: Eye,
    title: 'Monitoring & Auditability',
    description:
      'Route AI usage through approved gateways with logging and oversight, so you can evidence who used what, when, and why when a regulator or customer asks.',
  },
  {
    icon: ScrollText,
    title: 'Regulatory Alignment',
    description:
      'Map your programme to the EU AI Act, ISO 42001, and the NIST AI Risk Management Framework, turning emerging obligations into a practical control set.',
  },
];

const benefits = [
  'Safe, confident adoption of AI across the business',
  'Demonstrable control for customers, auditors, and regulators',
  'Reduced risk of data leakage through unapproved AI tools',
  'A clear, enforceable AI policy people actually follow',
  'Readiness for the EU AI Act, ISO 42001, and NIST AI RMF',
  'Board visibility of both AI risk and AI value',
];

const process = [
  {
    step: '01',
    title: 'Discover & Inventory',
    description:
      'We find where AI is already being used across the organisation - sanctioned and unsanctioned - and establish an honest baseline of your current maturity.',
  },
  {
    step: '02',
    title: 'Classify & Prioritise',
    description:
      'We tier AI use cases by risk, identifying what must be blocked immediately, what needs controls, and what can proceed with standard terms.',
  },
  {
    step: '03',
    title: 'Control & Enforce',
    description:
      'We implement policies, technical guardrails, and approval workflows that make safe AI use the path of least resistance rather than a barrier.',
  },
  {
    step: '04',
    title: 'Monitor & Improve',
    description:
      'We put logging, review cadences, and metrics in place so governance matures from ad hoc to managed to optimised over time.',
  },
];

export default function AiGovernanceServicePage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'AI Governance',
    description:
      'Govern AI adoption with confidence. AI policy, risk classification, model and vendor assurance, monitoring, and alignment with the EU AI Act, ISO 42001, and NIST AI RMF.',
    url: 'https://thecisonetwork.com/services/ai-governance',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="AI Governance"
        subtitle="Govern AI adoption with confidence. We help you move from ad hoc AI use to managed, auditable, and compliant AI operations - without slowing innovation."
        eyebrow="Our Services"
        backgroundImage={getServiceCard('ai-governance')?.image}
        backgroundAlt={getServiceCard('ai-governance')?.imageAlt}
        seoTitle="AI Governance | The CISO Network"
        seoDescription="Fractional CISO AI governance services - AI policy, risk classification, model and vendor assurance, monitoring, and alignment with the EU AI Act, ISO 42001, and NIST AI RMF."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Most AI Governance Is Written, Not Enforced
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Many organisations have an AI policy, but far fewer have the operational controls
                  to back it up. Without enforcement, written intentions can quickly fall out of
                  step with how AI is actually being used across the business - from everyday
                  productivity tools to systems built into products and workflows.
                </p>
                <p>
                  This is not a compliance problem - it is a control problem, and it is the
                  CISO&apos;s control problem. When an AI-generated decision goes wrong, a breach
                  originates from an AI tool, or a regulator asks for evidence of AI risk
                  management, a policy document will not protect you.
                </p>
                <p>
                  Our fractional CISOs translate high-level AI governance into specific, enforceable
                  controls - blocking the worst risks immediately and building operational maturity
                  over the following 90 days.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Brain className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  When You Need This
                </h3>
              </div>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {[
                    'Public AI tools are being used with company or customer data',
                    'You have an AI policy but no way to enforce it',
                    'AI features are being built without security review',
                    'Customers or investors are asking how you govern AI',
                    'You need to prepare for the EU AI Act or ISO 42001',
                    'AI adoption is outpacing your ability to manage the risk',
                    'The board wants assurance that AI is being used responsibly',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Offerings */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              AI Governance Services
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Practical controls that make safe, compliant AI adoption the default
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {offerings.map((offering, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal/10 flex items-center justify-center mb-6">
                  <offering.icon className="h-6 w-6 text-teal" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {offering.title}
                </h3>
                <p className="text-muted-foreground">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid sm:grid-cols-2 gap-6">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal font-bold text-sm">{i + 1}</span>
                    </div>
                    <p className="text-muted-foreground">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                The Benefits of Governed AI
              </h2>
              <p className="text-muted-foreground mb-6">
                Organisations that govern AI early gain a competitive advantage - they can adopt
                faster because they can adopt safely. Governance is the enabler, not the brake.
              </p>
              <div className="p-6 bg-background rounded-xl border border-border">
                <h4 className="font-heading font-semibold text-foreground mb-3">
                  Typical Engagement
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="text-foreground">4-8 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deliverable</span>
                    <span className="text-foreground">AI governance framework & controls</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Output</span>
                    <span className="text-foreground">Enforced, auditable AI operations</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Our Approach
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              From policy on paper to control in practice, in 90 days
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-heading font-bold text-teal/10 mb-4">{step.step}</div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8 text-center">
              Typical Deliverables
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: 'AI Acceptable Use Policy',
                  description:
                    'A clear, enforceable policy defining prohibited, controlled, and self-service AI use across the organisation',
                },
                {
                  title: 'AI Use-Case Register & Risk Tiering',
                  description:
                    'A living inventory of AI use cases classified by risk, with defined controls and approval routes for each tier',
                },
                {
                  title: 'Model & Vendor Assurance Framework',
                  description:
                    'Due-diligence criteria and scoring for AI vendors and models, covering data handling, security, and exit provisions',
                },
                {
                  title: 'Data Handling & DLP Controls for AI',
                  description:
                    'Technical and procedural controls that keep sensitive data out of unapproved models and detect leakage at the point of use',
                },
                {
                  title: 'AI Governance Operating Model',
                  description:
                    'Roles, review cadences, and board reporting structures that keep AI governance running after the engagement ends',
                },
              ].map((deliverable, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-background rounded-xl">
                  <div className="h-6 w-6 rounded-full bg-teal flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">
                      {deliverable.title}
                    </h4>
                    <p className="text-muted-foreground text-sm">{deliverable.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6">
                <Activity className="h-4 w-4" />
                Complementary Services
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                AI Governance Sits Within Your Wider Programme
              </h2>
              <p className="text-muted-foreground mb-6">
                AI governance is most effective when integrated with your compliance framework and
                reported clearly to the board. Our fractional CISOs connect AI oversight to your
                regulatory obligations and executive reporting.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/services/compliance"
                  className="inline-flex items-center text-teal font-medium hover:underline"
                >
                  Compliance &amp; Governance <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/services/board-reporting"
                  className="inline-flex items-center text-teal font-medium hover:underline"
                >
                  Board Reporting <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-border">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center mb-6">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                AI Readiness Check
              </h3>
              <p className="text-muted-foreground mb-6">
                Not sure where your AI exposure is? Our rapid readiness check discovers where AI is
                already in use, assesses your current controls, and maps a prioritised path to
                managed, compliant AI operations.
              </p>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">1-2 weeks</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Deliverable</span>
                  <span className="font-medium text-foreground">Exposure map &amp; roadmap</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Output</span>
                  <span className="font-medium text-foreground">Prioritised action plan</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Govern AI With Confidence
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your AI governance needs and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can help you adopt AI safely.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
